/**
 * src/data/franchiseClusters.ts
 *
 * Categorized universe and franchise groupings matching classic multi-column index boards.
 * 100% of slugs are verified canonical subcategories.
 */

export interface FranchiseLink {
  name: string;
  slug: string;
  categoryPrefix: string;
  hasChildren?: boolean;
}

export interface FranchisePillar {
  title: string;
  shortTitle: string;
  icon: string;
  accent: string;
  image: string;
  description: string;
  franchises: FranchiseLink[];
}

export const POP_CULTURE_PILLARS: FranchisePillar[] = [
  {
    title: "Tabletop RPGs & Lore",
    shortTitle: "Tabletop RPGs",
    icon: "🎲",
    accent: "text-amber-400",
    image: "/images/universes/tabletop.jpg",
    description: "Tabletop RPG systems, world lore, and campaign races.",
    franchises: [
      { name: "Dungeons & Dragons", slug: "dnd-character-names", categoryPrefix: "best", hasChildren: true },
      { name: "D&D Races & NPCs", slug: "dnd-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Daggerheart", slug: "daggerheart-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Pathfinder Races", slug: "pathfinder-aasimar-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Warhammer Fantasy", slug: "warhammer-dwarf-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Warhammer 40,000", slug: "warhammer-daemons-of-chaos-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Shadowrun", slug: "shadowrun-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "The Dark Eye", slug: "the-dark-eye-dwarf-names", categoryPrefix: "pop-culture" },
      { name: "Rift Races", slug: "rift-bahmi-names", categoryPrefix: "pop-culture" },
      { name: "Starfinder", slug: "starfinder-android-names", categoryPrefix: "places", hasChildren: true },
    ],
  },
  {
    title: "Fantasy Sagas & Literature",
    shortTitle: "Fantasy Sagas",
    icon: "📜",
    accent: "text-emerald-400",
    image: "/images/universes/fantasy.jpg",
    description: "High fantasy book universes, wizarding worlds, and mythic epics.",
    franchises: [
      { name: "Lord of the Rings", slug: "lotr-hobbit-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Harry Potter & Wizarding", slug: "hp-wizard-names", categoryPrefix: "fantasy", hasChildren: true },
      { name: "Game of Thrones (ASOIAF)", slug: "got-westeros-names", categoryPrefix: "other-names", hasChildren: true },
      { name: "A Court of Thorns & Roses", slug: "a-court-of-thorns-and-roses-names", categoryPrefix: "other-names" },
      { name: "The Witcher", slug: "the-witcher-aedirnian-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Wheel of Time", slug: "wheel-of-time-names", categoryPrefix: "other-names" },
      { name: "Mistborn (Cosmere)", slug: "mistborn-kandra-names", categoryPrefix: "other-names" },
      { name: "Stormlight Archive", slug: "stormlight-archive-names", categoryPrefix: "other-names" },
      { name: "Grishaverse", slug: "grishaverse-fjerdan-names", categoryPrefix: "other-names" },
      { name: "Percy Jackson", slug: "percy-jackson-demigod-names", categoryPrefix: "fantasy" },
      { name: "Inheritance Cycle (Eragon)", slug: "inheritance-cycle-dragon-names", categoryPrefix: "fantasy" },
      { name: "The Chronicles of Narnia", slug: "narnia-dwarf-names", categoryPrefix: "fantasy" },
      { name: "Arthurian Legend", slug: "arthurian-names", categoryPrefix: "pop-culture" },
      { name: "Lovecraftian Cthulhu", slug: "lovecraftian-names", categoryPrefix: "other-names" },
    ],
  },
  {
    title: "Sci-Fi & Space Operas",
    shortTitle: "Sci-Fi & Space",
    icon: "🚀",
    accent: "text-blue-400",
    image: "/images/universes/scifi.jpg",
    description: "Galactic empires, starfighters, alien coalitions, and cybernetics.",
    franchises: [
      { name: "Star Wars Races & Factions", slug: "sw-mandalorian-names", categoryPrefix: "other-names", hasChildren: true },
      { name: "Star Wars Planets & Lore", slug: "sw-planet-names", categoryPrefix: "places", hasChildren: true },
      { name: "Star Trek Races", slug: "star-trek-vulcan-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Dune Universe", slug: "dune-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Halo Universe", slug: "halo-forerunner-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Mass Effect", slug: "asari-names", categoryPrefix: "other-names", hasChildren: true },
      { name: "StarCraft", slug: "starcraft-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Doctor Who", slug: "doctor-who-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Destiny (Guardians & Hive)", slug: "destiny-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Cyberpunk 2077", slug: "cyberpunk-nick-names", categoryPrefix: "fantasy" },
      { name: "Transformers (Cybertron)", slug: "transformers-names", categoryPrefix: "pop-culture" },
    ],
  },
  {
    title: "Anime, Manga & Animation",
    shortTitle: "Anime & Manga",
    icon: "⛩️",
    accent: "text-rose-400",
    image: "/images/universes/anime.jpg",
    description: "Shonen legends, shinobi clans, titan shifters, and elemental benders.",
    franchises: [
      { name: "Avatar: The Last Airbender", slug: "avatar-tla-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Attack on Titan (Titans)", slug: "aot-titans", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Naruto (Shinobi Clans)", slug: "naruto-character-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Bleach (Shinigami & Hollows)", slug: "bleach-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "One Piece (Pirates & Marines)", slug: "one-piece-character-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Jujutsu Kaisen", slug: "jujutsu-kaisen-cursed-technique-names", categoryPrefix: "other-names" },
      { name: "Demon Slayer (Corps & Kizuki)", slug: "demon-slayer-names", categoryPrefix: "pop-culture" },
      { name: "Dragon Ball (Saiyans & Aliens)", slug: "dragon-ball-races", categoryPrefix: "pop-culture" },
      { name: "Fairy Tail", slug: "fairy-tail-names", categoryPrefix: "pop-culture" },
      { name: "Fullmetal Alchemist", slug: "fullmetal-alchemist-names", categoryPrefix: "other-names" },
      { name: "JoJo's Bizarre Adventure", slug: "jojo-bizarre-adventure-names", categoryPrefix: "pop-culture" },
      { name: "My Hero Academia (Quirks)", slug: "my-hero-academia-quirks", categoryPrefix: "other-names" },
      { name: "RWBY (Semblances & Weapons)", slug: "rwby-names", categoryPrefix: "pop-culture" },
    ],
  },
  {
    title: "Gaming Worlds & MMORPGs",
    shortTitle: "Gaming Worlds",
    icon: "🎮",
    accent: "text-purple-400",
    image: "/images/universes/gaming.jpg",
    description: "Epic video game franchises, RPGs, MMO realms, and fantasy kingdoms.",
    franchises: [
      { name: "World of Warcraft Races", slug: "wow-ankoan-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "The Elder Scrolls (Tamriel)", slug: "elder-scrolls-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "The Legend of Zelda (Hyrule)", slug: "zelda-deity-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Pokemon (Species & Towns)", slug: "pokemon-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Fallout (Wasteland)", slug: "fallout-names", categoryPrefix: "pop-culture" },
      { name: "Dark Souls & Elden Lore", slug: "dark-souls-names", categoryPrefix: "pop-culture" },
      { name: "Final Fantasy XIV", slug: "ffxiv-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Genshin Impact (Teyvat)", slug: "genshin-impact-constellations", categoryPrefix: "pop-culture" },
      { name: "Monster Hunter (Wyverns)", slug: "monster-hunter-elder-dragon-names", categoryPrefix: "fantasy" },
      { name: "Magic: The Gathering (Planes)", slug: "mtg-angel-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "RuneScape (Gielinor)", slug: "runescape-dwarf-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "League of Legends (Runeterra)", slug: "league-of-legends-celestial-dragon-names", categoryPrefix: "fantasy" },
      { name: "Minecraft (Mobs & Villages)", slug: "minecraft-villager-names", categoryPrefix: "pop-culture" },
      { name: "Diablo (Sanctuary)", slug: "diablo-names", categoryPrefix: "pop-culture" },
    ],
  },
  {
    title: "Comics, Superheroes & Cult",
    shortTitle: "Comics & Heroes",
    icon: "⚡",
    accent: "text-yellow-400",
    image: "/images/universes/superhero.jpg",
    description: "Caped crusaders, super villains, secret societies, and urban legends.",
    franchises: [
      { name: "DC Comics (Krypton & Gotham)", slug: "dc-comics-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Marvel Comics (Mutants & Asgard)", slug: "marvel-celestial-names", categoryPrefix: "pop-culture", hasChildren: true },
      { name: "Superhero & Team Names", slug: "superhero-team-names", categoryPrefix: "pop-culture" },
      { name: "Super Villain & Nemeses", slug: "villain-names", categoryPrefix: "pop-culture" },
      { name: "Hellaverse (Hazbin/Helluva)", slug: "hellaverse-demon-names", categoryPrefix: "fantasy" },
      { name: "Borderlands", slug: "borderlands-names", categoryPrefix: "pop-culture" },
      { name: "Creepypasta & Cryptids", slug: "creepypasta-names", categoryPrefix: "pop-culture" },
      { name: "Sonic the Hedgehog", slug: "sonic-mobian-names", categoryPrefix: "other-names" },
      { name: "Power Rangers Teams", slug: "power-rangers-team-names", categoryPrefix: "other-names" },
      { name: "Bionicle", slug: "bionicle-names", categoryPrefix: "pop-culture" },
    ],
  },
];
